import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const ProjectContextHeader = ({ conversation, onToggleSidebar, isSidebarOpen }) => {
  const [isProjectDetailsOpen, setIsProjectDetailsOpen] = useState(false);

  // Mock stack details - to be replaced with real data from Supabase
  const projectDetails = {
    id: conversation?.id,
    title: conversation?.projectTitle,
    category: 'Web Development',
    budget: '$5,000 - $10,000',
    timeline: '3-4 weeks',
    status: 'Active',
    description: 'Looking for a professional e-commerce website with modern design, payment integration, inventory management, and mobile responsiveness.',
    requirements: [
      'Responsive design for all devices',
      'Payment gateway integration (Stripe, PayPal)',
      'Product catalog with search and filters',
      'Admin dashboard for inventory management',
      'SEO optimization',
      'SSL certificate and security features'
    ],
    attachments: [
      { name: 'Stack_Brief.pdf', size: '2.4 MB', type: 'pdf' },
      { name: 'Brand_Guidelines.zip', size: '15.8 MB', type: 'zip' },
      { name: 'Reference_Sites.txt', size: '1.2 KB', type: 'txt' }
    ]
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return 'FileText';
      case 'zip':
        return 'Archive';
      case 'txt':
        return 'File';
      case 'doc': case'docx':
        return 'FileText';
      case 'jpg': case'jpeg': case'png': case'gif':
        return 'Image';
      default:
        return 'File';
    }
  };

  return (
    <div className="border-b border-border bg-background">
      {/* Main Header */}
      <div className="flex items-center justify-between p-4">
        {/* Left Section - Vendor Info */}
        <div className="flex items-center space-x-3">
          {/* Mobile Sidebar Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            className="md:hidden h-8 w-8"
          >
            <Icon name={isSidebarOpen ? 'X' : 'Menu'} size={16} />
          </Button>

          {/* Desktop Sidebar Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            className="hidden md:flex h-8 w-8"
          >
            <Icon name={isSidebarOpen ? 'PanelLeftClose' : 'PanelLeftOpen'} size={16} />
          </Button>

          {/* Vendor Profile */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                <Image
                  src={conversation?.vendorImage}
                  alt={`${conversation?.vendorName} profile picture`}
                  className="w-full h-full object-cover"
                />
              </div>
              {conversation?.isOnline && (
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-background rounded-full"></div>
              )}
            </div>

            <div>
              <h2 className="font-semibold text-foreground">{conversation?.vendorName}</h2>
              <p className="text-sm text-muted-foreground">
                {conversation?.isOnline ? 'Online' : 'Last seen 2 hours ago'}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsProjectDetailsOpen(!isProjectDetailsOpen)}
            className={cn(
              "h-8 w-8",
              isProjectDetailsOpen && "bg-primary/10 text-primary"
            )}
          >
            <Icon name="Info" size={16} />
          </Button>

          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Icon name="Phone" size={16} />
          </Button>

          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Icon name="Video" size={16} />
          </Button>

          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Icon name="MoreVertical" size={16} />
          </Button>
        </div>
      </div>
      {/* Project Context Bar */}
      <div className="px-4 py-2 bg-muted/30 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="Briefcase" size={14} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{projectDetails?.title}</span>
            </div>
            
            <div className={cn(
              "px-2 py-1 rounded-full text-xs font-medium border",
              getStatusColor(projectDetails?.status)
            )}>
              {projectDetails?.status}
            </div>

            <div className="hidden md:flex items-center space-x-4 text-sm text-muted-foreground">
              <span>Budget: {projectDetails?.budget}</span>
              <span>Timeline: {projectDetails?.timeline}</span>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsProjectDetailsOpen(!isProjectDetailsOpen)}
            className="text-xs"
          >
            <Icon name={isProjectDetailsOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="mr-1" />
            {isProjectDetailsOpen ? 'Hide Details' : 'Show Details'}
          </Button>
        </div>
      </div>
      {/* Collapsible Stack Details */}
      {isProjectDetailsOpen && (
        <div className="px-4 py-4 border-t border-border bg-muted/10">
          <div className="space-y-4">
            {/* Stack Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Category</h4>
                <p className="text-sm text-muted-foreground">{projectDetails?.category}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Budget Range</h4>
                <p className="text-sm text-muted-foreground">{projectDetails?.budget}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Expected Timeline</h4>
                <p className="text-sm text-muted-foreground">{projectDetails?.timeline}</p>
              </div>
            </div>

            {/* Stack Description */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Stack Description</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {projectDetails?.description}
              </p>
            </div>

            {/* Requirements */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Key Requirements</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                {projectDetails?.requirements?.map((requirement, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="mt-0.5 text-green-600 flex-shrink-0" />
                    <span>{requirement}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Attachments */}
            {projectDetails?.attachments && projectDetails?.attachments?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Stack Files</h4>
                <div className="space-y-2">
                  {projectDetails?.attachments?.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-background border border-border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center space-x-3">
                        <Icon name={getFileIcon(file?.type)} size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">{file?.name}</p>
                          <p className="text-xs text-muted-foreground">{file?.size}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Icon name="Download" size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectContextHeader;